/**
 * Time Clock v2 — the employee check-in screen (rebuilt Oct 29 2026).
 *
 * A tablet at the door / the Time Clock app / the back office's /time-clock: the device is
 * signed in to the restaurant; each employee finds their name (or types their phone), enters
 * their staff PIN and clocks in, starts / ends a paid or unpaid break, or clocks out. Today's
 * hours are on the screen. Refusals are the server's own words (already clocked in, no open
 * shift, wrong PIN with the tries left, PIN locked). Offline, the punch is kept on the device
 * with its time and sent when the connection is back (lib/timeclock/punchQueue).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, Briefcase, CheckCircle2, Clock, Coffee, CloudOff, Delete, History,
  LogIn, LogOut, Phone, Play, RefreshCw, Search, UtensilsCrossed, User, Wifi,
} from 'lucide-react';
import { useTimeclock } from '../context/TimeclockContext';
import { ACTION_LABEL, STATE_LABEL } from '@/lib/timeclock/api';
import { deviceId, newClientPunchId, queuePunch } from '@/lib/timeclock/punchQueue';
import { fmtDuration, fmtTime } from '@/lib/timeclock/time';

const IDLE_MS = 45000;
const DONE_MS = 7000;

function useNow(tz) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return {
    time: now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone: tz || undefined }),
    date: now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz || undefined }),
  };
}

const formatPhone = (value) => {
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const STATE_STYLE = {
  OFF: 'bg-slate-700/60 text-slate-200 ring-slate-500/40',
  WORKING: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/40',
  ON_BREAK: 'bg-amber-500/15 text-amber-300 ring-amber-400/40',
};

function Keypad({ onDigit, onBack, onClear, testid }) {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'];
  return (
    <div className="grid grid-cols-3 gap-3" data-testid={testid}>
      {keys.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => (k === 'back' ? onBack() : k === 'clear' ? onClear() : onDigit(k))}
          className={`h-16 rounded-2xl text-2xl font-semibold transition active:scale-95 ${
            k === 'clear' || k === 'back'
              ? 'bg-slate-800/70 text-slate-300 hover:bg-slate-700 text-base'
              : 'bg-slate-800 text-white hover:bg-slate-700 shadow-inner shadow-black/30'}`}
          data-testid={`key-${k}`}
          aria-label={k === 'back' ? 'Delete digit' : k === 'clear' ? 'Clear' : k}
        >
          {k === 'back' ? <Delete className="w-6 h-6 mx-auto" /> : k === 'clear' ? 'Clear' : k}
        </button>
      ))}
    </div>
  );
}

function BigAction({ icon: Icon, label, hint, tone, onClick, disabled, testid }) {
  const tones = {
    green: 'from-emerald-500 to-emerald-600 hover:from-emerald-400 shadow-emerald-900/40',
    red: 'from-rose-500 to-rose-600 hover:from-rose-400 shadow-rose-900/40',
    amber: 'from-amber-500 to-amber-600 hover:from-amber-400 shadow-amber-900/40',
    sky: 'from-sky-500 to-sky-600 hover:from-sky-400 shadow-sky-900/40',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-4 w-full rounded-2xl bg-gradient-to-br ${tones[tone]} px-5 py-5 text-left text-white shadow-lg transition active:scale-[0.98] disabled:opacity-50`}
      data-testid={testid}
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15"><Icon className="w-7 h-7" /></span>
      <span>
        <span className="block text-xl font-semibold">{label}</span>
        {hint && <span className="block text-sm text-white/80">{hint}</span>}
      </span>
    </button>
  );
}

export default function ClockPage({ onHistory = null, title = 'Time Clock' }) {
  const { api, auth, me, roster, online, pending, syncing, syncNow, timezone, restaurantId, loadError } = useTimeclock();
  const clock = useNow(timezone);
  const [step, setStep] = useState('who'); // who | pin | actions | offline | done
  const [mode, setMode] = useState('name');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [chooser, setChooser] = useState(null); // 'break' | {roles, action}
  const [done, setDone] = useState(null);
  const idle = useRef(null);

  const reset = useCallback(() => {
    setStep('who'); setPicked(null); setPhone(''); setPin(''); setInfo(null); setError(null);
    setChooser(null); setDone(null); setSearch(''); setBusy(false);
  }, []);

  const touch = useCallback(() => {
    clearTimeout(idle.current);
    if (step !== 'who') idle.current = setTimeout(reset, step === 'done' ? DONE_MS : IDLE_MS);
  }, [step, reset]);
  useEffect(() => { touch(); return () => clearTimeout(idle.current); }, [touch]);

  const who = useMemo(() => (picked ? { staff_id: picked.id } : { phone }), [picked, phone]);
  const whoName = picked?.display_name || info?.employee?.display_name || (phone ? phone : 'Employee');

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (roster || []).filter((e) => !s || e.display_name.toLowerCase().includes(s));
  }, [roster, search]);

  const goOffline = () => { setError(null); setStep('offline'); };

  const submitPin = async () => {
    if (pin.length < 4) { setError({ message: 'Enter your PIN (4–8 digits).' }); return; }
    setBusy(true); setError(null);
    try {
      const data = await api.identify({ ...who, pin });
      setInfo(data);
      setStep('actions');
    } catch (e) {
      if (e.network) {
        if (picked || phone.replace(/\D/g, '').length === 10) goOffline();
        else setError({ message: 'No connection. Pick your name so the punch can be saved on this device.' });
      } else {
        setError({ message: e.message, code: e.code });
        setPin('');
      }
    } finally {
      setBusy(false);
    }
  };

  const finish = (status, message, today) => {
    setDone({ status, message, today });
    setStep('done');
    setPin('');
  };

  const saveOffline = async (action, extra = {}, clientPunchId = null, at = null) => {
    try {
      await queuePunch({
        auth, restaurantId, action, breakPaid: !!extra.break_paid, roleId: extra.role_id || null,
        who: { staffId: picked?.id ?? null, staffName: whoName, phone: picked ? null : phone, pin },
        clientPunchId, at,
      });
      finish('queued', `${ACTION_LABEL[action]} saved on this device at ${fmtTime(new Date().toISOString(), timezone)}. ` +
        'It is sent when the connection is back; your manager reviews it if it does not line up.');
    } catch (e) {
      setError({ message: `Could not save the punch on this device: ${e.message}` });
    }
  };

  const doPunch = async (action, extra = {}) => {
    setChooser(null);
    if (step === 'offline') { await saveOffline(action, extra); return; }
    setBusy(true); setError(null);
    const clientPunchId = newClientPunchId();
    const at = new Date();
    try {
      const res = await api.punch({ ...who, pin, action, client_punch_id: clientPunchId, device_id: deviceId(), ...extra });
      finish(res.status, res.message, res.state_after?.today);
    } catch (e) {
      if (e.network) {
        await saveOffline(action, extra, clientPunchId, at);
      } else if (e.code === 'role_required') {
        setChooser({ roles: e.detail.roles, action });
      } else {
        setError({ message: e.message, code: e.code });
      }
    } finally {
      setBusy(false);
    }
  };

  const startClockIn = () => {
    const roles = info?.employee?.roles || [];
    if (roles.length > 1) setChooser({ roles, action: 'CLOCK_IN' });
    else doPunch('CLOCK_IN');
  };

  // ------------------------------------------------------------ render pieces

  const header = (
    <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/5 bg-slate-950/60 backdrop-blur">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 ring-1 ring-emerald-400/30">
          <Clock className="w-5 h-5 text-emerald-300" />
        </div>
        <div className="min-w-0">
          <p className="text-white font-semibold leading-tight truncate">{title}</p>
          <p className="text-xs text-slate-400 truncate">{me?.restaurant?.name || 'Restaurant'}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ring-1 ${online ? 'bg-emerald-500/10 text-emerald-300 ring-emerald-400/30' : 'bg-amber-500/10 text-amber-300 ring-amber-400/30'}`} data-testid="online-badge">
          {online ? <Wifi className="w-3.5 h-3.5" /> : <CloudOff className="w-3.5 h-3.5" />}
          {online ? 'Online' : 'Offline — punches are kept on this device'}
        </span>
        {pending > 0 && (
          <button type="button" onClick={syncNow} disabled={!online || syncing}
            className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/15 px-3 py-1 text-xs text-sky-200 ring-1 ring-sky-400/30 disabled:opacity-60"
            data-testid="pending-badge">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> {pending} waiting to send
          </button>
        )}
        {onHistory && (
          <button type="button" onClick={onHistory} className="rounded-xl p-2 text-slate-400 hover:bg-white/5 hover:text-white" aria-label="Punches on this device" data-testid="history-btn">
            <History className="w-5 h-5" />
          </button>
        )}
      </div>
    </header>
  );

  const errorBox = error && (
    <div className={`flex items-start gap-3 rounded-2xl px-4 py-3 text-sm ring-1 ${error.code === 'pin_locked' ? 'bg-rose-500/10 text-rose-200 ring-rose-400/40' : 'bg-amber-500/10 text-amber-100 ring-amber-400/30'}`} role="alert" data-testid="clock-error">
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
      <span>{error.message}</span>
    </div>
  );

  const backTo = (to) => (
    <button type="button" onClick={() => { setError(null); setChooser(null); if (to === 'who') reset(); else setStep(to); }}
      className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="back-btn">
      <ArrowLeft className="w-4 h-4" /> {to === 'who' ? 'Not you?' : 'Back'}
    </button>
  );

  let body;
  if (step === 'who') {
    body = (
      <div className="space-y-5">
        <div className="flex rounded-2xl bg-slate-900 p-1 ring-1 ring-white/5">
          {[['name', 'Find your name', User], ['phone', 'Use your phone number', Phone]].map(([m, label, Icon]) => (
            <button key={m} type="button" onClick={() => { setMode(m); setError(null); }}
              className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium transition ${mode === m ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
              data-testid={`mode-${m}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        {mode === 'name' ? (
          <>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search your name"
                className="w-full h-14 rounded-2xl bg-slate-900 pl-12 pr-4 text-lg text-white placeholder:text-slate-500 ring-1 ring-white/10 focus:outline-none focus:ring-emerald-400/50"
                data-testid="roster-search" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[46vh] overflow-y-auto pr-1" data-testid="roster">
              {filtered.map((e) => (
                <button key={e.id} type="button" onClick={() => { setPicked(e); setStep('pin'); setError(null); }}
                  className="rounded-2xl bg-slate-900 px-4 py-4 text-left ring-1 ring-white/5 transition hover:ring-emerald-400/40 hover:bg-slate-800 active:scale-[0.98]"
                  data-testid={`staff-${e.id}`}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400/30 to-sky-400/20 text-white font-semibold">
                    {e.display_name.slice(0, 1)}
                  </span>
                  <span className="mt-2 block text-white font-medium truncate">{e.display_name}</span>
                  <span className="block text-xs text-slate-400 capitalize truncate">{(e.roles?.map((r) => r.name).join(' · ')) || e.role}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <p className="col-span-full py-8 text-center text-slate-400">
                  {(roster || []).length === 0
                    ? (online ? 'No active employees yet. A manager adds them in Back Office → Employees.' : 'The staff list has not been loaded on this device yet — use your phone number.')
                    : 'No name matches.'}
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="mx-auto max-w-sm space-y-4">
            <div className="h-16 rounded-2xl bg-slate-900 ring-1 ring-white/10 flex items-center justify-center text-3xl font-mono tracking-wider text-white" data-testid="phone-display">
              {phone || <span className="text-slate-600">(555) 555-5555</span>}
            </div>
            <Keypad testid="phone-pad" onDigit={(d) => setPhone(formatPhone(phone + d))}
              onBack={() => setPhone(formatPhone(phone.replace(/\D/g, '').slice(0, -1)))} onClear={() => setPhone('')} />
            <button type="button" disabled={phone.replace(/\D/g, '').length !== 10}
              onClick={() => { setPicked(null); setStep('pin'); setError(null); }}
              className="w-full h-14 rounded-2xl bg-emerald-500 text-lg font-semibold text-white hover:bg-emerald-400 disabled:opacity-40"
              data-testid="phone-next">Next</button>
          </div>
        )}
        {errorBox}
      </div>
    );
  } else if (step === 'pin') {
    body = (
      <div className="mx-auto max-w-sm space-y-5">
        {backTo('who')}
        <div className="text-center">
          <p className="text-slate-400 text-sm">Hi</p>
          <p className="text-2xl font-semibold text-white">{whoName}</p>
          <p className="text-slate-400 text-sm mt-1">Enter your staff PIN</p>
        </div>
        <div className="flex justify-center gap-3" data-testid="pin-dots">
          {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
            <span key={i} className={`h-4 w-4 rounded-full ${i < pin.length ? 'bg-emerald-400' : 'bg-slate-700'}`} />
          ))}
        </div>
        {errorBox}
        <Keypad testid="pin-pad" onDigit={(d) => { setError(null); setPin((p) => (p + d).slice(0, 8)); }}
          onBack={() => setPin((p) => p.slice(0, -1))} onClear={() => setPin('')} />
        <button type="button" onClick={submitPin} disabled={busy || pin.length < 4}
          className="w-full h-14 rounded-2xl bg-emerald-500 text-lg font-semibold text-white hover:bg-emerald-400 disabled:opacity-40"
          data-testid="pin-submit">{busy ? 'Checking…' : 'Continue'}</button>
      </div>
    );
  } else if (step === 'actions' && info) {
    const st = info.state;
    const allowed = info.allowed_actions || [];
    body = (
      <div className="mx-auto max-w-lg space-y-5">
        {backTo('who')}
        <div className="rounded-3xl bg-slate-900/80 p-5 ring-1 ring-white/5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-2xl font-semibold text-white" data-testid="employee-name">{info.employee.name}</p>
              <p className="text-sm text-slate-400 capitalize">{info.entry?.role_name || info.employee.role}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-sm ring-1 ${STATE_STYLE[st]}`} data-testid="state-badge">{STATE_LABEL[st]}</span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl bg-slate-800/60 p-3">
              <p className="text-xs text-slate-400">Clocked in</p>
              <p className="text-white font-medium">{info.entry ? fmtTime(info.entry.clock_in, timezone) : '—'}</p>
            </div>
            <div className="rounded-2xl bg-slate-800/60 p-3">
              <p className="text-xs text-slate-400">Today</p>
              <p className="text-white font-medium" data-testid="today-hours">{fmtDuration(info.today?.worked_seconds)}</p>
            </div>
            <div className="rounded-2xl bg-slate-800/60 p-3">
              <p className="text-xs text-slate-400">Breaks</p>
              <p className="text-white font-medium">{fmtDuration((info.today?.unpaid_break_seconds || 0) + (info.today?.paid_break_seconds || 0))}</p>
            </div>
          </div>
          {info.on_break && (
            <p className="mt-3 text-sm text-amber-200">On a {info.on_break.is_paid ? 'paid' : 'unpaid'} break since {fmtTime(info.on_break.start_at, timezone)}</p>
          )}
        </div>
        {errorBox}
        {chooser === 'break' ? (
          <div className="space-y-3" data-testid="break-chooser">
            <p className="text-slate-300 text-sm">Which break?</p>
            <BigAction icon={Coffee} tone="sky" label="Paid rest break" hint="Short break — stays paid" onClick={() => doPunch('BREAK_START', { break_paid: true })} disabled={busy} testid="break-paid" />
            <BigAction icon={UtensilsCrossed} tone="amber" label="Unpaid meal break" hint="Taken out of your hours" onClick={() => doPunch('BREAK_START', { break_paid: false })} disabled={busy} testid="break-unpaid" />
          </div>
        ) : chooser?.roles ? (
          <div className="space-y-3" data-testid="role-chooser">
            <p className="text-slate-300 text-sm">Which job are you working?</p>
            {chooser.roles.map((r) => (
              <BigAction key={r.id} icon={Briefcase} tone="green" label={r.name} onClick={() => doPunch('CLOCK_IN', { role_id: r.id })} disabled={busy} testid={`role-${r.id}`} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {allowed.includes('CLOCK_IN') && <BigAction icon={LogIn} tone="green" label="Clock in" hint={clock.time} onClick={startClockIn} disabled={busy} testid="clock-in-btn" />}
            {allowed.includes('BREAK_START') && <BigAction icon={Coffee} tone="amber" label="Start break" hint="Paid rest or unpaid meal" onClick={() => setChooser('break')} disabled={busy} testid="break-start-btn" />}
            {allowed.includes('BREAK_END') && <BigAction icon={Play} tone="green" label="End break" hint="Back on the clock" onClick={() => doPunch('BREAK_END')} disabled={busy} testid="break-end-btn" />}
            {allowed.includes('CLOCK_OUT') && <BigAction icon={LogOut} tone="red" label="Clock out" hint="End your shift" onClick={() => doPunch('CLOCK_OUT')} disabled={busy} testid="clock-out-btn" />}
          </div>
        )}
        {info.punches_today?.length > 0 && (
          <div className="rounded-2xl bg-slate-900/60 p-4 ring-1 ring-white/5">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Today's punches</p>
            <ul className="space-y-1.5 text-sm">
              {info.punches_today.slice(0, 6).map((p) => (
                <li key={p.id} className="flex justify-between text-slate-300">
                  <span>{ACTION_LABEL[p.action]}{p.status === 'review' ? ' · for review' : ''}</span>
                  <span className="text-slate-400">{fmtTime(p.occurred_at, timezone)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  } else if (step === 'offline') {
    body = (
      <div className="mx-auto max-w-lg space-y-5">
        {backTo('who')}
        <div className="rounded-3xl bg-amber-500/10 p-5 ring-1 ring-amber-400/30">
          <div className="flex items-center gap-3">
            <CloudOff className="w-6 h-6 text-amber-300" />
            <div>
              <p className="text-white font-semibold">{whoName} — no connection</p>
              <p className="text-sm text-amber-100/80">Your punch is saved on this device with the time it was made and checked (PIN included) as soon as the connection is back.</p>
            </div>
          </div>
        </div>
        {errorBox}
        {chooser === 'break' ? (
          <div className="space-y-3">
            <BigAction icon={Coffee} tone="sky" label="Paid rest break" onClick={() => doPunch('BREAK_START', { break_paid: true })} testid="break-paid" />
            <BigAction icon={UtensilsCrossed} tone="amber" label="Unpaid meal break" onClick={() => doPunch('BREAK_START', { break_paid: false })} testid="break-unpaid" />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <BigAction icon={LogIn} tone="green" label="Clock in" onClick={() => doPunch('CLOCK_IN')} testid="clock-in-btn" />
            <BigAction icon={Coffee} tone="amber" label="Start break" onClick={() => setChooser('break')} testid="break-start-btn" />
            <BigAction icon={Play} tone="sky" label="End break" onClick={() => doPunch('BREAK_END')} testid="break-end-btn" />
            <BigAction icon={LogOut} tone="red" label="Clock out" onClick={() => doPunch('CLOCK_OUT')} testid="clock-out-btn" />
          </div>
        )}
      </div>
    );
  } else if (step === 'done' && done) {
    const ring = done.status === 'applied' ? 'bg-emerald-500/15 ring-emerald-400/40'
      : done.status === 'queued' ? 'bg-sky-500/15 ring-sky-400/40' : 'bg-amber-500/15 ring-amber-400/40';
    body = (
      <div className="mx-auto max-w-md text-center space-y-5 py-6" data-testid="punch-done">
        <div className={`mx-auto flex h-24 w-24 items-center justify-center rounded-full ring-1 ${ring}`}>
          {done.status === 'applied' ? <CheckCircle2 className="w-12 h-12 text-emerald-300" />
            : done.status === 'queued' ? <CloudOff className="w-12 h-12 text-sky-300" />
              : <AlertTriangle className="w-12 h-12 text-amber-300" />}
        </div>
        <p className="text-xl text-white font-semibold" data-testid="punch-message">{done.message}</p>
        {done.today && <p className="text-slate-300">Today: <span className="text-white font-medium">{fmtDuration(done.today.worked_seconds)}</span> worked</p>}
        <button type="button" onClick={reset} className="rounded-2xl bg-slate-800 px-6 py-3 text-white hover:bg-slate-700" data-testid="done-btn">Done</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[radial-gradient(ellipse_at_top,_rgba(16,185,129,0.12),_transparent_55%),linear-gradient(to_bottom,_#020617,_#0f172a)]" onPointerDown={touch} data-testid="time-clock">
      {header}
      <main className="flex-1 w-full max-w-3xl mx-auto px-5 py-6 space-y-6">
        <div className="text-center">
          <p className="text-5xl sm:text-6xl font-semibold tracking-tight text-white tabular-nums" data-testid="current-time">{clock.time}</p>
          <p className="mt-1 text-slate-400">{clock.date}</p>
        </div>
        {loadError && <p className="rounded-2xl bg-rose-500/10 px-4 py-3 text-sm text-rose-200 ring-1 ring-rose-400/30">{loadError}</p>}
        {body}
      </main>
      <footer className="px-5 py-4 text-center text-xs text-slate-500">
        Punches go straight to Payroll. A manager fixes a missed punch in Back Office → Timesheet.
      </footer>
    </div>
  );
}
